import React from 'react';
import { Select, Input, Button, Checkbox, Space } from 'antd';
import type { DataGeneratorType } from '@packages/shared';

const { Option } = Select;

interface ControlPanelProps {
  selectedDataType: DataGeneratorType;
  nodeCount: number;
  edgeCount: number;
  enableSearchGrayOut: boolean;
  connectionDepth: number;
  performanceMode: 'normal' | 'high';
  autoPerformanceMode: boolean;
  performanceThreshold: number;
  showDataPanel: boolean;
  loading: boolean;
  debug?: boolean;
  onDataTypeChange: (type: DataGeneratorType) => void;
  onNodeCountChange: (count: number) => void;
  onEdgeCountChange: (count: number) => void;
  onEnableSearchGrayOutChange: (enabled: boolean) => void;
  onConnectionDepthChange: (depth: number) => void;
  onPerformanceModeChange: (mode: 'normal' | 'high') => void;
  onAutoPerformanceModeChange: (auto: boolean) => void;
  onPerformanceThresholdChange: (threshold: number) => void;
  onSetTestData: (nodes: number, edges: number) => void;
  onGenerateNewData: () => void;
  onToggleDataPanel: () => void;
}

const ControlPanel: React.FC<ControlPanelProps> = ({
  selectedDataType,
  nodeCount,
  edgeCount,
  enableSearchGrayOut,
  connectionDepth,
  performanceMode,
  autoPerformanceMode,
  performanceThreshold,
  showDataPanel,
  loading,
  debug = false,
  onDataTypeChange,
  onNodeCountChange,
  onEdgeCountChange,
  onEnableSearchGrayOutChange,
  onConnectionDepthChange,
  onPerformanceModeChange,
  onAutoPerformanceModeChange,
  onPerformanceThresholdChange,
  onSetTestData,
  onGenerateNewData,
  onToggleDataPanel
}) => {
  return (
    <div className="bg-white shadow-md p-4 border-b">
      <div className="flex flex-wrap items-center gap-4">
        {/* Debug模式下的数据生成控件 */}
        {debug && (
          <>
            {/* 数据类型选择 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">数据类型:</label>
              <Select
                value={selectedDataType}
                onChange={onDataTypeChange}
                className="w-32"
                size="small"
              >
                <Option value="social">社交网络</Option>
                <Option value="academic">学术研究</Option>
                <Option value="business">商业关系</Option>
                <Option value="biological">生物医学</Option>
                <Option value="technology">技术栈</Option>
              </Select>
            </div>

            {/* 节点数量 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">节点数:</label>
              <Input
                type="number"
                value={nodeCount}
                onChange={(e) => onNodeCountChange(Number(e.target.value))}
                min={5}
                max={2000}
                className="w-20"
                size="small"
              />
            </div>

            {/* 边数量 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">边数:</label>
              <Input
                type="number"
                value={edgeCount}
                onChange={(e) => onEdgeCountChange(Number(e.target.value))}
                min={5}
                max={5000}
                className="w-20"
                size="small"
              />
            </div>
          </>
        )}

        {/* 搜索置灰配置 */}
        <div className="flex items-center gap-2">
          <Checkbox
            checked={enableSearchGrayOut}
            onChange={(e) => onEnableSearchGrayOutChange(e.target.checked)}
          >
            <span className="text-sm text-gray-700">搜索置灰</span>
          </Checkbox>
          {enableSearchGrayOut && (
            <div className="flex items-center gap-1">
              <label className="text-xs text-gray-600">阶数:</label>
              <Input
                type="number"
                value={connectionDepth}
                onChange={(e) => onConnectionDepthChange(Number(e.target.value))}
                min={1}
                max={5}
                className="w-12"
                size="small"
              />
            </div>
          )}
        </div>


        {/* 性能模式控制 */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">性能模式:</label>
          <div className="flex items-center gap-2">
            <Select
              value={performanceMode}
              onChange={onPerformanceModeChange}
              disabled={autoPerformanceMode}
              className="w-28"
              size="small"
            >
              <Option value="normal">普通模式</Option>
              <Option value="high">高性能模式</Option>
            </Select>
            <Checkbox
              checked={autoPerformanceMode}
              onChange={(e) => onAutoPerformanceModeChange(e.target.checked)}
            >
              <span className="text-sm text-gray-700">自动</span>
            </Checkbox>
          </div>
        </div>

        {/* 性能阈值设置 */}
        {autoPerformanceMode && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">阈值:</label>
            <Input
              type="number"
              value={performanceThreshold}
              onChange={(e) => onPerformanceThresholdChange(Number(e.target.value))}
              min={50}
              max={1000}
              step={50}
              className="w-16"
              size="small"
            />
          </div>
        )}

        {/* Debug模式下的快速测试按钮 */}
        {debug && (
          <div className="flex items-center gap-1">
            <Button
              onClick={() => onSetTestData(50, 75)}
              size="small"
              className="text-xs"
              loading={loading}
              disabled={loading}
            >
              小型
            </Button>
            <Button
              onClick={() => onSetTestData(200, 300)}
              size="small"
              type="primary"
              ghost
              className="text-xs"
              loading={loading}
              disabled={loading}
            >
              中型
            </Button>
            <Button
              onClick={() => onSetTestData(1000, 1500)}
              size="small"
              danger
              className="text-xs"
              loading={loading}
              disabled={loading}
            >
              大型
            </Button>
          </div>
        )}

        {/* Debug模式下的重新生成按钮 */}
        {debug && (
          <Button
            onClick={onGenerateNewData}
            type="primary"
            size="small"
            loading={loading}
          >
            重新生成
          </Button>
        )}

        {/* 数据面板切换按钮 */}
        <Button
          onClick={onToggleDataPanel}
          type="primary"
          ghost
          size="small"
        >
          {showDataPanel ? '隐藏数据' : '显示数据'}
        </Button>
      </div>
    </div>
  );
};

export default ControlPanel;